import React, {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getTableDivHeaderHeight, Styles } from "./styled";
import { getGridTemplateColumns, getLeftOffset } from "./untils";
import CustomVerticalScrollBar from "../CustomVerticalScrollBar";
import CustomHorizontalScrollBar from "../CustomHorizontalScrollBar";
import { Checkbox, Empty, Spin, Tooltip } from "antd";
import type { Column } from "./types";
import { useTableColumnLayout } from "./useTableColumnLayout";
import { FONT_SIZE, ROUTES, TEXT_SCALE_PROPS } from "@lhd/constants";
import { DISPLAY_SIZES, MODE } from "@lhd/types";
import Button from "@lhd/components/Button";
import HandIcon from "../../assets/images/hand.svg";
import EwResize from "../../assets/images/ew-resize.svg";
import { AxiosResponse } from "axios";

export type { Column } from "./types";

const getTransitionDurationMs = (el: HTMLElement) => {
  const style = getComputedStyle(el);
  const durations = style.transitionDuration
    .split(",")
    .map((value) => parseFloat(value) || 0);
  const delays = style.transitionDelay
    .split(",")
    .map((value) => parseFloat(value) || 0);

  return (
    durations.reduce(
      (max, duration, index) =>
        Math.max(max, (duration + (delays[index] ?? delays[0] ?? 0)) * 1000),
      0,
    ) + 50
  );
};

const measureAfterTransition = (el: HTMLElement, callback: () => void) => {
  let cancelled = false;
  let debounceTimer: number | undefined;

  const run = () => {
    if (cancelled) return;
    requestAnimationFrame(() => {
      if (!cancelled) callback();
    });
  };

  const onTransitionEnd = (event: TransitionEvent) => {
    if (event.target !== el) return;
    if (debounceTimer) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(run, 16);
  };

  const fallbackMs = getTransitionDurationMs(el) || 350;
  el.addEventListener("transitionend", onTransitionEnd);
  const fallbackTimer = window.setTimeout(run, fallbackMs);

  return () => {
    cancelled = true;
    el.removeEventListener("transitionend", onTransitionEnd);
    if (debounceTimer) window.clearTimeout(debounceTimer);
    if (fallbackTimer) window.clearTimeout(fallbackTimer);
  };
};

export type TableDivProps<T> = {
  columns: Column<T>[];
  dataSource: T[];
  rowKey: keyof T | ((record: T) => string);
  className?: string;
  isPDF?: boolean;
  pdfGridTemplateColumns?: string;
  renderRow?: (
    record: T,
    index: number,
    row: (dragHandleProps?: any) => React.ReactNode,
  ) => React.ReactNode;
  rowClassName?: (record: T, index: number) => string;
  rowStyle?: (record: T, index?: number) => CSSProperties;
  scrollVertical?: "fit";
  style?: CSSProperties;
  heightOffset?: number;
  isCollapse?: boolean;
  isLoading?: boolean;
  onScroll?: (direction: "horizontal" | "vertical") => void;
  columnResize?: boolean;
  columnReorder?: boolean;
  columnWidths?: Record<string, number>;
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
  onColumnWidthsEnd?: (widths: Record<string, number>) => void;
  onColumnCheckboxEnd?: (checked: Record<string, boolean>) => void;
  columnOrder?: string[];
  onColumnOrderChange?: (order: string[]) => void;
  isOpenQuickView?: boolean;
  displaySize?: TEXT_SCALE_PROPS["displaySize"];
  pageMode?: MODE;
  resetLayoutEditAsync?: () => Promise<AxiosResponse>;
};

type SortableHeaderProps = {
  id: string;
  className: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  columnResize: boolean;
  minX: number;
  maxX: number;
  isHeaderResizeDisabled?: boolean;
  showRowEditLayout?: boolean;
  onResizeStart: (
    e: React.PointerEvent,
    colKey: string,
    cellEl: HTMLElement | null,
  ) => void;
};

function SortableHeaderCell({
  id,
  className,
  style,
  children,
  columnResize,
  onResizeStart,
  minX,
  maxX,
  isHeaderResizeDisabled,
  showRowEditLayout,
}: SortableHeaderProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !isHeaderResizeDisabled });
  const cellRef = useRef<HTMLDivElement | null>(null);
  const setRefs = (node: HTMLDivElement | null) => {
    cellRef.current = node;
    setNodeRef(node);
  };

  // Remove scale and x from transform, keep only y for vertical movement, clamped to not exceed scroll width
  const modifiedTransform = transform
    ? {
        ...transform,
        scaleX: 1,
        scaleY: 1,
        x: Math.max(minX, Math.min(maxX, transform.x)),
        y: 0,
      }
    : null;

  return (
    <div
      ref={setRefs}
      className={className}
      style={{
        ...style,
        transform: modifiedTransform
          ? CSS.Transform.toString(modifiedTransform)
          : undefined,
        transition,
        zIndex: isDragging ? 2 : undefined,
        opacity: isDragging ? 0.92 : undefined,
      }}
      {...attributes}
    >
      <div className="table-div__cell_header_inner">
        <div
          className={`${
            showRowEditLayout
              ? "table-div__col-drag-custom"
              : "table-div__col-drag"
          }`}
          {...listeners}
        >
          {children}
        </div>
      </div>
      {columnResize && (
        <div
          className="table-div__col-resize-handle"
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onResizeStart(e, id, cellRef.current);
          }}
        />
      )}
    </div>
  );
}
const SortableColumnButton = ({ id }: { id: string }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
      }}
      {...attributes}
      {...listeners}
    >
      <Button
        label={"並替"}
        height={20}
        icon={<img src={HandIcon} alt="hand" />}
      />
    </div>
  );
};

function TableDivInner<T extends object>(
  {
    columns: columnsRaw,
    dataSource,
    rowKey,
    className,
    renderRow,
    rowClassName,
    style,
    scrollVertical,
    heightOffset = 0,
    isCollapse,
    isLoading,
    onScroll,
    columnResize = false,
    columnReorder = false,
    columnWidths: columnWidthsProp,
    onColumnWidthsChange,
    onColumnWidthsEnd,
    onColumnCheckboxEnd,
    columnOrder: columnOrderProp,
    onColumnOrderChange,
    isPDF,
    pdfGridTemplateColumns,
    isOpenQuickView,
    rowStyle,
    pageMode,
    displaySize = DISPLAY_SIZES.SMALL,
    resetLayoutEditAsync,
  }: TableDivProps<T>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const refTable = useRef<HTMLDivElement | null>(null);
  const [heightBodyTable, setHeightBodyTable] = useState(0);
  const [pinLeftWidth, setPinLeftWidth] = useState(0);
  const [widthScrollBar, setWidthScrollBar] = useState(0);
  const [heightScrollBar, setHeightScrollBar] = useState(0);
  const [hasScrollV, setHasScrollV] = useState(false);
  const [hasScrollH, setHasScrollH] = useState(false);
  const [autoWidths, setAutoWidths] = useState<Record<string, number>>({});
  const isFirstDisplaySizeRender = useRef(true);
  const headerHeight = getTableDivHeaderHeight(displaySize);

  const isEditLayout = pageMode === MODE.EDIT_LAYOUT;
  const showRowEditLayout = [ROUTES.BUSINESS_MANUALS].some((path) =>
    window.location.pathname.includes(path),
  );
  const {
    columnsWithWidths,
    effectiveFlexibleOrder,
    allFlexibleKeys,
    widths,
    sensors,
    onColumnDragEnd,
    onResizeStart,
    widthResizing,
    columnVisibility,
    onColumnVisibilityChange,
  } = useTableColumnLayout({
    columnsRaw,
    columnResize,
    columnReorder,
    columnWidths: columnWidthsProp,
    onColumnWidthsChange,
    onColumnWidthsEnd,
    onColumnCheckboxEnd,
    columnOrder: columnOrderProp,
    onColumnOrderChange,
  });

  const handleResetLayout = async () => {
    if (!resetLayoutEditAsync) return;
    try {
      await resetLayoutEditAsync();
    } catch (error) {
      console.error("Failed to reset layout:", error);
    }
  };

  const getRowKey = (record: T) =>
    typeof rowKey === "function" ? rowKey(record) : String(record[rowKey]);

  const columns = useMemo(() => {
    const result: Column<T>[] = [];

    columnsWithWidths.forEach((c, index) => {
      const nextColumn = columnsWithWidths[index + 1];
      const isNextFixedLeft = nextColumn?.fixed === "left";

      const column = {
        ...c,
        checkbox: columnVisibility[c.key] ?? c.checkbox ?? false,
      };

      const colPush: any[] = [column];

      if (!isNextFixedLeft && column.fixed === "left") {
        colPush.push({
          ...column,
          fixed: undefined,
          title: () => <div />,
          render: () => <div />,
          width: widthScrollBar,
          className: "scroll_cell",
          key: "scroll",
        });
      }

      result.push(...colPush);
    });

    return result;
  }, [columnsWithWidths, columnVisibility, widthScrollBar]);

  const columnsForGrid = columns.map((col) =>
    col.autoWidth && autoWidths[col.key]
      ? { ...col, width: autoWidths[col.key] }
      : col,
  );
  const gridTemplateColumns = getGridTemplateColumns(
    columnsForGrid,
    widthScrollBar,
    widthResizing,
  );
  const activeGridTemplateColumns = isPDF
    ? pdfGridTemplateColumns || `repeat(${columns.length}, minmax(0, 1fr))`
    : gridTemplateColumns;

  const renderCellStyle = (col: Column<T>, colIndex: number) =>
    col.fixed === "left"
      ? {
          position: "sticky" as const,
          left: getLeftOffset(columns, colIndex),
          zIndex: 100,
        }
      : undefined;

  const isHeaderResizeDisabled = showRowEditLayout ? false : columnResize;

  const getPinLeftWidthByDOM = () => {
    if (!refTable.current) return 0;
    const cells = refTable.current.querySelectorAll(
      ".table-div__cell_header.fixed-left",
    );

    let width = 0;
    cells.forEach((el) => {
      width += (el as HTMLElement).offsetWidth;
    });

    return width;
  };

  const styleHeight: CSSProperties =
    scrollVertical === "fit" && dataSource.length > 0
      ? { height: isOpenQuickView ? "calc(50vh - 175px)" : heightBodyTable }
      : {};

  useLayoutEffect(() => {
    if (!refTable.current) return;

    const calcHeight = () => {
      if (!refTable.current) return;
      const rect = refTable.current.getBoundingClientRect();
      const containerQuickview = refTable.current.closest(
        ".container-quickview",
      );
      let availableHeight = document.documentElement.clientHeight;
      if (containerQuickview) {
        availableHeight = containerQuickview.getBoundingClientRect().bottom;
      }
      const height =
        availableHeight -
        rect.top -
        heightOffset +
        (containerQuickview ? 11 : 0);
      const minHeight = containerQuickview ? 150 : 400;
      setHeightBodyTable(Math.max(height, minHeight));
    };

    calcHeight();
    const ro = new ResizeObserver(calcHeight);
    ro.observe(refTable.current);
    window.addEventListener("resize", calcHeight);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", calcHeight);
    };
  }, [heightOffset]);

  useEffect(() => {
    const el = refTable.current;
    if (!el) return;

    const checkScroll = () => {
      setHasScrollV(el.scrollHeight > el.clientHeight);
      setHasScrollH(el.scrollWidth > el.clientWidth);
    };

    checkScroll();

    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);

    el.addEventListener("scroll", checkScroll);

    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", checkScroll);
    };
  }, [dataSource, columns, displaySize]);

  const handleAutoWidth = useCallback(() => {
    if (!refTable.current) return;
    const autoWidthCols = columns.filter((c) => c.autoWidth);
    if (autoWidthCols.length === 0) return;

    const tableEl = refTable.current;
    const newWidths: Record<string, number> = {};
    autoWidthCols.forEach((col) => {
      const cells = tableEl.querySelectorAll<HTMLElement>(
        `[data-col-key="${col.key}"]`,
      );
      let maxWidth = 0;
      cells.forEach((cell) => {
        const prev = cell.style.width;
        cell.style.width = "max-content";
        maxWidth = Math.max(maxWidth, cell.scrollWidth);
        cell.style.width = prev;
      });
      if (maxWidth > 0) newWidths[col.key] = maxWidth;
    });

    setAutoWidths((prev) => {
      const changed = autoWidthCols.some(
        (col) =>
          newWidths[col.key] !== undefined &&
          prev[col.key] !== newWidths[col.key],
      );
      return changed ? { ...prev, ...newWidths } : prev;
    });
  }, [columns]);

  useLayoutEffect(() => {
    handleAutoWidth();
  }, [dataSource, columns, handleAutoWidth]);

  useLayoutEffect(() => {
    setPinLeftWidth(getPinLeftWidthByDOM());
  }, [dataSource, columns, autoWidths]);

  useEffect(() => {
    if (isFirstDisplaySizeRender.current) {
      isFirstDisplaySizeRender.current = false;
      return;
    }
    const el = refTable.current;
    if (!el) return;

    return measureAfterTransition(el, () => {
      handleAutoWidth();
      setPinLeftWidth(getPinLeftWidthByDOM());
    });
  }, [displaySize, handleAutoWidth]);

  useEffect(() => {
    if (!hasScrollV) {
      setWidthScrollBar(0);
    }
  }, [hasScrollV]);

  useEffect(() => {
    if (!hasScrollH) {
      setHeightScrollBar(0);
    }
  }, [hasScrollH]);

  const getDragLimit = (columns: any[], currentKey: string) => {
    const normalCols = columns.filter((col) => !col.fixed);

    const currentIndex = normalCols.findIndex((col) => col.key === currentKey);

    let minX = 0;
    let maxX = 0;

    // left
    for (let i = 0; i < currentIndex; i++) {
      minX -= normalCols[i].width || 0;
    }

    // right
    for (let i = currentIndex + 1; i < normalCols.length; i++) {
      maxX += normalCols[i].width || 0;
    }

    return { minX, maxX };
  };

  const renderHeaderCell = (col: Column<T>, colIndex: number) => {
    const baseClass = `table-div__cell table-div__cell_header ${
      col.fixed === "left" ? "fixed-left" : ""
    }`;
    const cellStyle = renderCellStyle(col, colIndex);
    const isScroll = col.key === "scroll";
    const isFixedLeft = col.fixed === "left";
    const canSort =
      columnReorder &&
      !isScroll &&
      !isFixedLeft &&
      allFlexibleKeys.includes(col.key);

    const cellWidth = isPDF
      ? undefined
      : autoWidths[col.key] ||
        widths[col.key] ||
        (typeof col.width === "number" ? col.width : undefined);

    if (canSort) {
      return (
        <SortableHeaderCell
          key={col.key}
          id={col.key}
          className={baseClass}
          style={{
            ...cellStyle,
            width: cellWidth ? `${cellWidth}px` : undefined,
            flexShrink: 0,
          }}
          columnResize={isHeaderResizeDisabled}
          onResizeStart={onResizeStart}
          {...getDragLimit(columns, col.key)}
          isHeaderResizeDisabled={isHeaderResizeDisabled}
          showRowEditLayout={showRowEditLayout}
        >
          <Tooltip title={col.title}>{col.title}</Tooltip>
        </SortableHeaderCell>
      );
    }

    return (
      <div
        key={col.key}
        className={baseClass}
        style={{
          ...cellStyle,
          width: cellWidth ? `${cellWidth}px` : undefined,
        }}
      >
        {!isScroll ? (
          <div className="table-div__cell_header_inner">
            <div
              className={`${
                showRowEditLayout
                  ? "table-div__col-drag-custom"
                  : "table-div__col-drag"
              }`}
              style={{ cursor: "default" }}
            >
              <Tooltip title={col.title}>{col.title}</Tooltip>
            </div>
          </div>
        ) : (
          col.title
        )}
        {col.resizeAble && !isScroll && (
          <div
            className="table-div__col-resize-handle"
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onResizeStart(e, col.key, e.currentTarget.parentElement);
            }}
          />
        )}
      </div>
    );
  };

  const headerRow = (
    <div className="table-div__header">
      {columns.map((col, colIndex) => renderHeaderCell(col, colIndex))}
    </div>
  );

  const EDIT_LAYOUT_ROWS = [
    { colIndex: 1, label: "列表示設定" },
    { colIndex: 1, label: "列切替" },
    { colIndex: 1, label: "列幅調整" },
  ];

  const editLayoutRows = EDIT_LAYOUT_ROWS.map((row, index) => (
    <div
      key={`edit-layout-${index}`}
      className="table-div__row table-div__layout-row"
    >
      {columns.map((col, colIndex) => {
        const isTargetColumn = [0, 1, 2].includes(colIndex);

        return (
          <div
            key={col.key}
            className={`table-div__cell ${
              col.fixed === "left" ? "fixed-left" : ""
            } ${col.className ?? ""}`}
            style={{
              ...renderCellStyle(col, colIndex),
              justifyContent: !isTargetColumn ? "center" : "start",
            }}
          >
            {isTargetColumn ? (
              colIndex === row.colIndex ? (
                row.label
              ) : (
                ""
              )
            ) : index === 0 ? (
              <Checkbox
                checked={columnVisibility[col.key] ?? true}
                onChange={(e) =>
                  onColumnVisibilityChange(col.key, e.target.checked)
                }
              />
            ) : index === 1 ? (
              <SortableColumnButton id={col.key} />
            ) : index === 2 ? (
              <>
                {columnResize && (
                  <div
                    className="table-div__col-resize-handle-custom"
                    onPointerDown={(e) => {
                      const cell = e.currentTarget.parentElement;
                      if (cell) {
                        onResizeStart(e, col.key, cell);
                      }
                    }}
                  >
                    <span className="table-div__resize-icon">
                      <img src={EwResize} alt="ew" />
                    </span>
                  </div>
                )}
              </>
            ) : (
              ""
            )}
          </div>
        );
      })}
    </div>
  ));

  const editLayoutButtonRow = (
    <div className="table-div__row table-div__layout-row table-div__layout-button-row">
      <div
        className="table-div__cell fixed-left"
        style={{
          position: "sticky",
          left: 0,
          zIndex: 101,
          gridColumn: "1 / 3",
          justifyContent: "start",
          background: "#fff",
        }}
      >
        <Button
          label="レイアウトを初期値に戻す"
          className="fixed-left"
          height={24}
          onClick={handleResetLayout}
          // loading={isResettingLayout}
        />
      </div>
    </div>
  );
  const isAllCheckboxFalse = columns
    .slice(3)
    .every((col) => col.checkbox === false);
  return (
    <Spin spinning={!!isLoading}>
      <div
        style={{
          position: "relative",
        }}
      >
        {hasScrollV && (
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: pinLeftWidth - 1,
              display: "flex",
              justifyContent: "end",
              zIndex: 105,
            }}
          >
            <CustomVerticalScrollBar
              element={refTable.current}
              onGetWidthScrollVertical={(h) => {
                setWidthScrollBar(h);
              }}
              heightOffset={headerHeight + heightScrollBar}
            />
          </div>
        )}
        <Styles
          gridTemplateColumns={activeGridTemplateColumns}
          headerHeight={headerHeight}
          heightScrollBar={heightScrollBar + 1}
          ref={(node) => {
            refTable.current = node;
            if (typeof ref === "function") ref(node);
            else if (ref) (ref as any).current = node;
          }}
          style={{
            ...style,
            fontSize: FONT_SIZE[displaySize],
            ...styleHeight,
            maxHeight: isPDF
              ? "none"
              : heightBodyTable - (headerHeight + heightScrollBar),
          }}
          className={`table-div ${className ?? ""} ${
            hasScrollV ? "has-scroll-v" : ""
          }`}
        >
          {isAllCheckboxFalse && showRowEditLayout && !isEditLayout ? (
            <div className="empty-notification-container">
              <div className="empty-notification-content">
                <p className="empty-notification-title">
                  表示する列がありません。
                </p>
                <p className="empty-notification-subtitle">
                  レイアウト編集モードで表示する列を設定してください。
                </p>
              </div>
            </div>
          ) : (
            <>
              {columnReorder ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={onColumnDragEnd}
                >
                  <SortableContext
                    items={effectiveFlexibleOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerRow}
                    {isEditLayout && showRowEditLayout && (
                      <>
                        {editLayoutRows}
                        {editLayoutButtonRow}
                      </>
                    )}
                  </SortableContext>
                </DndContext>
              ) : (
                <>
                  {headerRow}
                  {isEditLayout && showRowEditLayout && (
                    <>
                      {editLayoutRows}
                      {editLayoutButtonRow}
                    </>
                  )}
                </>
              )}
              {hasScrollH && (
                <div
                  style={{
                    position: "sticky",
                    top: headerHeight,
                    left: 0,
                    width: "100%",
                    display: "flex",
                    justifyContent: "end",
                    zIndex: 105,
                    backgroundColor: "white",
                  }}
                  className="row_scroll"
                >
                  <CustomHorizontalScrollBar
                    element={refTable.current}
                    onGetHeightScrollVertical={(h) => {
                      setHeightScrollBar(h);
                    }}
                    onScroll={() => {
                      onScroll?.("horizontal");
                    }}
                    widthOffset={pinLeftWidth + widthScrollBar}
                  />
                </div>
              )}
              <div
                className={`table-div__body`}
                hidden={isCollapse ? true : undefined}
              >
                {dataSource.map((record, index) => {
                  const key = getRowKey(record);
                  const isLastItem = index === dataSource.length - 1;
                  const rowContent = (dragHandleProps?: any) => (
                    <div
                      className={`table-div__row ${
                        rowClassName ? rowClassName(record, index) : ""
                      }`}
                      style={{
                        ...rowStyle?.(record, index),
                      }}
                    >
                      {columns.map((col, colIndex) => {
                        const value = col.dataIndex
                          ? (record as any)[col.dataIndex]
                          : undefined;

                        return (
                          <div
                            key={col.key}
                            className={`table-div__cell ${
                              col.fixed === "left" ? "fixed-left" : ""
                            } ${col.className ?? ""} ${
                              isLastItem ? "last_item" : ""
                            } `}
                            style={renderCellStyle(col, colIndex)}
                            data-col-key={col.autoWidth ? col.key : undefined}
                          >
                            {col.render
                              ? col.render(
                                  value,
                                  record,
                                  index,
                                  dragHandleProps,
                                )
                              : String(value ?? "")}
                          </div>
                        );
                      })}
                    </div>
                  );

                  return renderRow ? (
                    <React.Fragment key={key}>
                      {renderRow(record, index, rowContent)}
                    </React.Fragment>
                  ) : (
                    <React.Fragment key={key}>{rowContent()}</React.Fragment>
                  );
                })}
              </div>
              {dataSource.length === 0 && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ background: "#fff", margin: 0, padding: 20 }}
                />
              )}
            </>
          )}
        </Styles>
      </div>
    </Spin>
  );
}

const TableDiv = forwardRef(TableDivInner) as <T extends object>(
  props: TableDivProps<T> & { ref?: ForwardedRef<HTMLDivElement> },
) => JSX.Element;

export default TableDiv;
